<script setup>
import { computed, ref, watch, toRef, inject } from "vue";
import { useStore } from "vuex";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { useRouter, useRoute } from "vue-router";

import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/v4/dropdown-menu";

import {
  IconCircleFilled,
  IconUserCircle,
  IconBrandPnpm,
  IconBellSchool,
  IconLockDollar,
  IconLogout,
} from "@tabler/icons-vue";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/v4/dialog";
import { Textarea } from "@/components/v4/form";

import { Button } from "@/components/v4/button";

import { axiosAuthConfig } from "@/config";
import { httpClientOmni } from "@/libraries";

import { StorageService } from "@/services/auth";

import urlTemp from "@/constants/urlTemp";

const props = defineProps({
  defaultAgentStatus: String,
});

const queryClient = useQueryClient();
const router = useRouter();
const route = useRoute();
const store = useStore();
const reason = ref("");
const selectedState = ref("offline");
const isOpenReasonModal = ref(false);

const agentStatus = toRef(props.defaultAgentStatus);
const userData = inject("userData")?.value;
const jssip = inject("jssip", null);

const hashed = computed(() => store.getters["appId"]?.hashed);
const decodeUserId = computed(
  () => store.getters["localStorage"].decodedUserId
);

const agentStateList = [
  {
    key: "online",
    label: "Online",
    color: "var(--success-400)",
  },
  {
    key: "break",
    label: "Break",
    color: "var(--warning-400)",
  },
  {
    key: "offline",
    label: "Offline",
    color: "var(--neutral-400)",
  },
];

const dropdownNavigation = {
  profile: { name: "settings.general.profile-setting", params: undefined },
  workspace: () =>
    setTimeout(
      () => (window.location.href = `${urlTemp.value}/workspace`),
      300
    ),
  alert: {
    name: "settings.notification-sound",
    params: {
      appId: route.params.appId,
    },
  },
  billing: {
    name: "billing",
    params: {
      appId: hashed.value,
    },
  },
};

const mutationChangeStatus = useMutation({
  mutationKey: ["AGENT_UPDATE_ONLINE"],
  mutationFn: async (state) => {
    const response = await httpClientOmni.post("/agent/change-status", {
      agent_id: decodeUserId.value,
      state,
      reason_offline: reason.value,
    });
    return response;
  },
  onSuccess: async (response) => {
    const newState = response.data.data?.state;
    agentStatus.value = newState;
    queryClient.setQueryData(["QUERY_AGENT_GET_ONLINE"], newState);
    reason.value = "";

    isOpenReasonModal.value = false;
  },
});

const handleLogout = async () => {
  jssip?.shutdownSip?.();
  let access_token;
  try {
    access_token = JSON.parse(localStorage.getItem("user") || "null")?.token;
  } catch {
    access_token = undefined;
  }

  if (access_token && axiosAuthConfig.baseURL) {
    const base = String(axiosAuthConfig.baseURL).replace(/\/$/, "");
    try {
      await fetch(`${base}/logout`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${access_token}`,
          "Content-Type": "application/json",
        },
      });
    } catch {
      // Network errors: server may be unreachable; still clear local session below
    }
  }

  localStorage.removeItem("user");

  router.replace("/auth/login");
};

const onChangeState = (state) => {
  if (state === "online") {
    mutationChangeStatus.mutate(state);
  } else {
    isOpenReasonModal.value = true;
    selectedState.value = state;
    agentStatus.value = props.defaultAgentStatus;
  }
};

watch(
  () => props.defaultAgentStatus,
  (newVal) => {
    agentStatus.value = newVal;
  },
  {
    once: true,
  }
);

const isMutating = computed(() => mutationChangeStatus.isPending);
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger>
      <img
        v-if="userData?.picture"
        class="vf-w-10 vf-h-10 vf-bg-neutral-200 vf-p-1 vf-rounded-full"
        :src="userData?.picture"
        alt="profile-picture"
      />
      <span
        v-else
        class="vf-h-10 vf-w-10 vf-bg-neutral-200 vf-rounded-full flex-center"
      >
        {{ acronymText(StorageService.get("name")) }}
      </span>
    </DropdownMenuTrigger>
    <DropdownMenuContent
      :align="'end'"
      :avoidCollisions="true"
      :sideOffset="16"
      class="vf-w-[180px]"
    >
      <DropdownMenuLabel>
        {{ truncateText(StorageService.get("name"), 21) }}
        <span class="vf-text-neutral-400 vf-text-xs vf-block vf-font-normal">{{
          StorageService.get("email")
        }}</span>
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuRadioGroup
        v-model="agentStatus"
        @update:model-value="onChangeState"
      >
        <DropdownMenuRadioItem
          v-for="state in agentStateList"
          :key="state.key"
          :value="state.key"
        >
          <span class="vf-flex vf-items-center vf-gap-x-2">
            <IconCircleFilled size="12" :color="state.color" />
            {{ state.label }}
          </span>
        </DropdownMenuRadioItem>
      </DropdownMenuRadioGroup>
      <DropdownMenuSeparator />
      <DropdownMenuItem @select="router.push(dropdownNavigation.profile)"
        ><IconUserCircle color="var(--neutral-400)" />Profile</DropdownMenuItem
      >
      <DropdownMenuItem @select="dropdownNavigation.workspace"
        ><IconBrandPnpm color="var(--neutral-400)" />Workspace</DropdownMenuItem
      >
      <DropdownMenuItem @select="router.push(dropdownNavigation.alert)"
        ><IconBellSchool color="var(--neutral-400)" />Alert</DropdownMenuItem
      >
      <DropdownMenuItem @select="router.push(dropdownNavigation.billing)"
        ><IconLockDollar color="var(--neutral-400)" />Billing</DropdownMenuItem
      >
      <DropdownMenuItem
        @select="handleLogout"
        class="vf-text-danger-400 hover:vf-text-danger-500"
        ><IconLogout color="var(--danger-400)" />Logout</DropdownMenuItem
      >
    </DropdownMenuContent>
  </DropdownMenu>
  <Dialog :open="isOpenReasonModal" @update:open="isOpenReasonModal = $event">
    <DialogContent class="sm:max-w-[425px]">
      <DialogHeader>
        <DialogTitle>Change Status</DialogTitle>
        <DialogDescription>
          Reason for changing status to:
          <span class="vf-font-semibold vf-capitalize">{{
            selectedState
          }}</span></DialogDescription
        >
      </DialogHeader>
      <div class="">
        <Textarea
          v-model="reason"
          class="vf-h-20"
          placeholder="Enter Reason.."
        />
      </div>
      <DialogFooter>
        <Button
          @click="mutationChangeStatus.mutate(selectedState)"
          type="button"
          variant="primary"
          :loading="isMutating.value"
        >
          Submit
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
